import { CheckCircle2, Microscope, ClipboardCheck, Repeat, PackageCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { IMAGES } from "@/data/site";

const PRACTICES = [
    {
        icon: Repeat,
        title: "Consistent Manufacturing",
        desc: "Defined process parameters and disciplined stage control keep every batch consistent with the last.",
    },
    {
        icon: Microscope,
        title: "Multi-Stage Inspection",
        desc: "Boards are visually inspected as they move through production, with final inspection before approval.",
    },
    {
        icon: ClipboardCheck,
        title: "Customer Specifications",
        desc: "Boards are manufactured strictly to the customer's design data and specification — never to assumptions.",
    },
    {
        icon: PackageCheck,
        title: "Reliable PCB Quality",
        desc: "Only boards that pass inspection and electrical testing are packed and dispatched to the customer.",
    },
];

export default function Quality() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="Quality"
                eyebrow="Consistency · Inspection · Specification"
                titleLines={["Quality", "Commitment."]}
                lead="Consistent manufacturing, inspection at every stage and boards built strictly to customer specifications."
            />

            <section className="bg-white py-20 md:py-28" data-testid="quality-commitment">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <div>
                        <FadeUp>
                            <Eyebrow>Our Approach to Quality</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Quality is a process, not a claim
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                For a PCB manufacturer, quality is decided long before final
                                inspection. It is decided at imaging, at etching, at lamination, at
                                drilling and at plating. That is why our approach is built around
                                consistent, controlled manufacturing — checking the work at each
                                stage rather than discovering problems at the end.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                                Every order is manufactured to the customer's own design data and
                                specification. Board details are confirmed at the enquiry stage, so
                                both sides know exactly what is being produced before production
                                begins.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                                The result is reliable PCB quality our customers can plan their
                                production around — batch after batch, order after order.
                            </p>
                        </FadeUp>
                    </div>
                    <FadeUp className="relative">
                        <img
                            src={IMAGES.factoryTesting}
                            alt="Flying probe testing and functional test work stations at Unimicron Technology India"
                            className="w-full border border-slate-200"
                        />
                        <div className="absolute -bottom-4 -right-4 hidden h-24 w-24 border-b-2 border-r-2 border-brand-red md:block" aria-hidden="true" />
                    </FadeUp>
                </div>
            </section>

            <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="quality-practices">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Quality Practices</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            How we keep PCB quality reliable
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="quality-practice-grid">
                        {PRACTICES.map((p, i) => (
                            <FadeUp key={p.title} delay={i * 0.08} className="group bg-white p-7 transition-colors duration-200 hover:bg-navy">
                                <p.icon className="h-6 w-6 text-brand-red" strokeWidth={1.5} />
                                <h3 className="mt-5 text-[16px] font-semibold text-navy transition-colors group-hover:text-white">{p.title}</h3>
                                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">{p.desc}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-20 md:py-28" data-testid="quality-checklist">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
                        <FadeUp>
                            <Eyebrow>What We Check</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Inspection points across production
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                From incoming data to packed boards, our checks follow the board
                                through production.
                            </p>
                        </FadeUp>
                        <div className="space-y-px bg-slate-200" data-testid="quality-checklist-items">
                            {[
                                "Gerber data and specification review before production",
                                "Copper pattern, drilling and plating inspection during production",
                                "Solder mask and legend verification",
                                "Electrical testing of completed boards",
                                "Final inspection against the customer's specification",
                                "Careful packing so boards arrive ready for assembly",
                            ].map((item, i) => (
                                <FadeUp key={item} delay={i * 0.05} className="flex items-start gap-3 bg-white p-4">
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                    <p className="text-[13.5px] leading-relaxed text-slate-700">{item}</p>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                    <FadeUp className="mt-16 border border-slate-200 bg-slate-50 p-8 md:p-10">
                        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
                            <p className="max-w-2xl text-[15px] leading-relaxed text-navy">
                                Need PCBs manufactured to your specification? Send your requirement
                                and Gerber data — we will confirm how we can support it.
                            </p>
                            <button
                                onClick={openQuote}
                                data-testid="quality-quote-btn"
                                className="shrink-0 border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                            >
                                Get a Quote
                            </button>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
